import { createAsyncThunk, createAction, createSlice } from "@reduxjs/toolkit";
import {AxiosError} from "axios";
import {DoctorRequestDto, DoctorResponseDto, Configuration, GlobalDoctorsApi} from "../api/client-axios";

import {ApiError} from "../model/apiError";
import {Problem} from "../model/problem";
import {initOtpState} from "./otpSlice";
import {apiConfiguration} from "./server";
import { applyRejection } from "./thunkError";

export interface DoctorNoOrgState {
    doctor?: DoctorResponseDto,
    status: "idle" | "loading" | "error" | "success" | "auth-error" | "saved";
    error?: string | Problem;
    code?: number;
}


const initialNoOrgDoctorState: DoctorNoOrgState = {
    status: "idle",
    error: ""
}

export const fetchNoOrgDoctor = createAsyncThunk<DoctorResponseDto, { doctorId: string }, {
    rejectValue: ApiError
}>('doctor/fetchNoOrgDoctor', async (args, thunkApi) => {
    try {
        const api = new GlobalDoctorsApi(apiConfiguration());
        if (args.doctorId === "new") {
            thunkApi.dispatch(initOtpState());
            return {} as DoctorResponseDto;
        }
        const response = await api.apiV1AdministrationNoOrgDoctorsIdGet(args.doctorId)
        return response.data;
    } catch (error: AxiosError | any) {
        return thunkApi.rejectWithValue({message: error?.response?.data, statusCode: error?.response?.status});
    }
});


export const saveNoOrgDoctor = createAsyncThunk<DoctorResponseDto, {
    doctorId: string,
    doctor: DoctorRequestDto
}, { rejectValue: ApiError }>('doctor/saveNoOrgDoctor', async (args, thunkApi) => {
    try {
        const api = new GlobalDoctorsApi(apiConfiguration());
        const response = await api.apiV1AdministrationNoOrgDoctorsIdPost(args.doctorId, args.doctor);
        return response.data;
    } catch (error: AxiosError | any) {
        return thunkApi.rejectWithValue({message: error?.response?.data, statusCode: error?.response?.status});
    }
});




export const updateNoOrgDoctorFields = createAction("doctor/updateNoOrgDoctorFields", (doctor: DoctorRequestDto) => {
    return {payload: doctor}
});
export const clearSaveNoOrgDoctorError = createAction('doctor/clearErrorNoOrgDoctor');
export const updateNoOrgDoctorPhoneNumber = createAction('doctor/updatePhoneNumberNoOrgDoctor', (phoneNumber: string) => {
    return {payload: phoneNumber};
});
export const initNoOrgDoctorState = createAction('doctor/initNoOrg');

const doctorNoOrgSlice = createSlice({
    name: 'doctorNoOrg',
    initialState: initialNoOrgDoctorState,
    reducers: {},
    extraReducers: builder => {
        builder
            .addCase(fetchNoOrgDoctor.pending, (state, _) => {
                state.status = "loading";
            })
            .addCase(fetchNoOrgDoctor.fulfilled, (state, action) => {
                state.doctor = action.payload;
                state.status = "success";
            })
            .addCase(fetchNoOrgDoctor.rejected, (state, action) => {
                applyRejection(state, action);
            })
            .addCase(saveNoOrgDoctor.pending, (state, _) => {
                state.status = "loading";
            })
            .addCase(saveNoOrgDoctor.fulfilled, (state, action) => {
                state.doctor = action.payload;
                state.status = "saved";
            })
            .addCase(saveNoOrgDoctor.rejected, (state, action) => {
                applyRejection(state, action);
            })
            .addCase(clearSaveNoOrgDoctorError, (state, action) => {
                state.status = "success";
                state.error = "";
            })
            .addCase(updateNoOrgDoctorPhoneNumber, (state, action) => {
                state.doctor!.userAccount = {
                    mobileNumber: action.payload,
                    emailAddress: state.doctor?.userAccount?.emailAddress
                };
            })
            .addCase(initNoOrgDoctorState, (state, action) => {
                state.status = "idle";
                state.doctor = {};
                state.code = undefined;
                state.error = undefined;
            })
            .addCase(updateNoOrgDoctorFields, (state, action) => {
                state.doctor = {
                    ...state.doctor,
                    ...action.payload as DoctorResponseDto
                };
            });

    }
});

export default doctorNoOrgSlice.reducer;
